import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';

import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynGridColumnConfigBase
}                                                                     from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynComponentComponent
}                                                                     from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  IPpPlanningWizardDetailsData,
  IPpPlanningWizardOverviewGridData,
  IPpPlanningWizardSelectionGridData,
  IPpProductListGridItem,
  IPpWizardProvider,
  PpWizardStates
}                                                                     from './pp-planning-wizard.types';
import {ApUtilService}                                                from '../../../ap-utils/service/ap-util.service';
import {
  PpPlanningWizardSelectionProvider
}                                                                     from './configs/pp-planning-wizard-selection.provider';
import {
  PpPlanningWizardDetailsProvider
}                                                                     from './configs/pp-planning-wizard-details.provider';
import {
  PpPlanningWizardOverviewProvider
}                                                                     from './configs/pp-planning-wizard-overview.provider';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {CampaignYearStore}                                            from '../../../stores/login/campaignyear.store';
import {
  PlantProtectionProductStore
}                                   from '../../../stores/base-data/plantprotection.product.store';
import {FormStore, IEntryComponent} from '../../../stores/layout/form.store';
import {debounceTime, map}          from 'rxjs/operators';
import {FieldStore}                                                   from '../../../stores/farm/field.store';
import {CropRotationStore}                                            from '../../../stores/farm/crop.rotation.store';
import {CropTypeStore}                                                from '../../../stores/base-data/crop.types.store';
import {
  PlantProtectionPlanBookStore
}                                                                     from '../../../stores/plant-protection/plant-protection-planbook.store';
import {SensorStore}                                                  from '../../../stores/sensor/sensor.store';
import {
  OperationModesEnum
}                                                                     from '../../../stores/common/operation-modes.store';
import {MapViewCurrentMenu, MapViewMode}                              from '../../../ap-interface';
import {MapViewStore}                                                 from '../../../stores/layout/mapview.store';
import {StatisticStore}                                               from '../../../stores/statistic/statistic.store';
import {LocationStrategy}                                             from '@angular/common';

@Component({
    selector: 'plant-protection-planning-wizard',
    template: `
    <ap-dyncomponent [loading$]="loading$"
      [headerIcon]="'ap-fungidetect-icon'"
      [columns]="columns$ | async"
      [fieldSets]="fieldSets$ | async"
      [errorKeys]="errorKeys$ | async"
      [canCreate]="false"
      [canSearch]="true"
      [items]="items$ | async"
      [pager]="pager">
      <div class="ap-form-actions" dynforms.action>
        <div apResponsive
          [sizeMd]="'25-100'"
          [style.text-align]="'left'">
          <button kendoButton
            [id]="'button_prev'"
            [type]="'button'"
            [class]="'button-important'"
            (apSingleClick)="handleStateChangeAction.emit(wizardSteps.Selection)">
            {{'Global_Back_With_Arrow' | translate }}
          </button>
        </div>
        <div apResponsive
          [sizeMd]="'50-100'"
          [style.text-align]="'center'">
          <ap-breadcrumbs-steper [columns]="breadCrumbsColumns"
            [selected]="1">
          </ap-breadcrumbs-steper>
        </div>
        <div apResponsive
          [sizeMd]="'25-100'"
          [style.text-align]="'right'">
          <button kendoButton
            [id]="'button_next'"
            [disabled]="(dynComponent.Valid$ | async) === false || (productListItems$.asObservable() | async)?.length <= 0"
            [type]="'button'"
            [themeColor]="'primary'"
            [class]="'button-important'"
            (apSingleClick)="dynComponent?.dynForm?.form?.valid && handleStateChangeAction.emit(wizardSteps.Overview)">
            {{'Global_Next_With_Arrow' | translate }}
          </button>
        </div>
      </div>
    </ap-dyncomponent>
    <ng-template #plantProtectionProductListTemplate>
      <ap-dyngrids
        [class]="(productListItems$.asObservable() | async)?.length <= 0 ? 'ap-form-element-error-border' : ''"
        class="pp-product-list ap-grid-max-height ap-form-element-border"
        id="SelectedProducts"
        [columns]="productListColumns"
        [items]="productListItems$.asObservable() | async"
        [pageable]="false"
        [groupable]="false"
        [hideHeader]="true"
        [checkboxDisabled]="true"
        [selectable]="{checkboxOnly: false, mode: 'single'}">
      </ap-dyngrids>
      @if ((productListItems$.asObservable() | async)?.length <= 0) {
        <div class="ap-error">
          <ap-responsive-text [key]="'Settings__Msg_Vali_Value_Required'"></ap-responsive-text>
        </div>
      }
    </ng-template>
    `,
    styles: [
        '.pp-product-list { height: 120px; display: block; }'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PpPlanningWizardComponent implements OnInit, AfterContentInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('Nutrients__SelectFieldsTitle');
  @ViewChild(ApDynComponentComponent, {static: true}) public dynComponent: ApDynComponentComponent;
  @ViewChild('plantProtectionProductListTemplate', {static: true}) public plantProtectionProductListTemplate: TemplateRef<any>;

  private _subscriptions: Subscription[] = [];
  private _stateModel: { [oldState: string]: { [newState: string]: (oldState: PpWizardStates) => void } } = {};
  private _activeWizardStep: PpWizardStates = PpWizardStates.Selection;
  private _isContinueActive = new BehaviorSubject<boolean>(false);
  private _itemSubscription: Subscription;
  private _statisticUpdateRequested = new EventEmitter<void>();

  public wizardSteps = PpWizardStates;
  public errorKeys$ = new BehaviorSubject<string[]>([]);
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public fieldSets$ = new BehaviorSubject<ApDynformsConfigFieldset[]>([]);
  public items$ = new Observable<IPpPlanningWizardSelectionGridData[] | IPpPlanningWizardOverviewGridData[]>(undefined);
  public pager: ApDynGridPagerConfigBase;
  public productListColumns: ApDynGridColumnConfigBase[] = [];
  public productListItems$: BehaviorSubject<IPpProductListGridItem[]> = new BehaviorSubject<IPpProductListGridItem[]>([]);
  public loading$: Observable<boolean>;
  public handleStateChangeAction: EventEmitter<any>;
  public selectionData: IPpPlanningWizardSelectionGridData[];
  public detailsData: IPpPlanningWizardDetailsData;
  public overviewData: IPpPlanningWizardOverviewGridData[];

  public breadCrumbsColumns = [
    PpWizardStates.Selection,
    PpWizardStates.Details,
    PpWizardStates.Overview
  ];

  constructor(private wizardSelectionProvider: PpPlanningWizardSelectionProvider,
              private wizardDetailsProvider: PpPlanningWizardDetailsProvider,
              private wizardOverviewProvider: PpPlanningWizardOverviewProvider,
              private fieldStore: FieldStore,
              private cropRotationStore: CropRotationStore,
              private cropTypeStore: CropTypeStore,
              private sensorStore: SensorStore,
              private campaignYearStore: CampaignYearStore,
              private plantProtectionProductStore: PlantProtectionProductStore,
              private plantProtectionPlanBookStore: PlantProtectionPlanBookStore,
              private formStore: FormStore,
              private mapViewStore: MapViewStore,
              private statisticStore: StatisticStore,
              private location: LocationStrategy) {
    this.handleStateChangeAction = ApUtilService.createEventEmitter(this.handleStateChange.bind(this), this._subscriptions);
    /*
     * Setup of plant protection wizard state machine...
     */
    this._stateModel[PpWizardStates.Cancel] = {};
    this._stateModel[PpWizardStates.Selection] = {};
    this._stateModel[PpWizardStates.Details] = {};
    this._stateModel[PpWizardStates.Overview] = {};
    this._stateModel[PpWizardStates.Save] = {};
    // Definition of actions when state changes: [oldState][newState] => (action) executed
    this._stateModel[PpWizardStates.Selection][PpWizardStates.Details] = this.initDetails.bind(this); // user continues from selection to details
    this._stateModel[PpWizardStates.Details][PpWizardStates.Overview] = this.initOverview.bind(this); // user switches from details to overview-tab
    this._stateModel[PpWizardStates.Overview][PpWizardStates.Save] = this.initSave.bind(this); // user clicks export in overview
    this._stateModel[PpWizardStates.Overview][PpWizardStates.Details] = this.initDetails.bind(this); // user goes back to details from overview
    this._stateModel[PpWizardStates.Details][PpWizardStates.Selection] = this.initSelection.bind(this); // user navigates from details back to selection
    this._stateModel[PpWizardStates.Selection][PpWizardStates.Cancel] = this.initLeave.bind(this); // leaving wizard
    this._stateModel[PpWizardStates.Save][PpWizardStates.Details] = this.initDetails.bind(this); // after failed export user goes back to details
    this._stateModel[PpWizardStates.Save][PpWizardStates.Save] = this.initSave.bind(this); // export retry by user
  }

  public ngOnInit(): void {
    // Disable ability to navigate back in browser
    history.pushState(null, document.title, location.href);
    history.back();
    history.forward();
    window.addEventListener('popstate', this.preventGoBack);

    this.sensorStore.loadSensorFieldData();
    if (!this.plantProtectionProductStore.Listen(s => s.loaded).value) {
      this.plantProtectionProductStore.loadPlantProtectionProducts(true);
    }
  }

  public ngAfterContentInit(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Listen(s => s.loading),
      this.cropRotationStore.Listen(s => s.historyLoading),
      this.campaignYearStore.Listen(s => s.loading),
      this.cropTypeStore.Listen(s => s.loading),
      this.plantProtectionProductStore.Listen(s => s.loading)
    ]).pipe(
      map((loadings) => loadings.some((l) => l))
    );
    // Wizard step's provider configuration setup:
    this.wizardSelectionProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.errorKeys$,
      this.handleStateChangeAction,
      this._isContinueActive,
      this.dynComponent);
    this.wizardDetailsProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.dynComponent.dynForm,
      this.plantProtectionProductListTemplate,
      this.productListItems$);
    this.wizardOverviewProvider.init(
      this.Caption$,
      this.fieldSets$,
      this.handleStateChangeAction,
      this._isContinueActive,
      this.dynComponent);
    this._subscriptions.push(this.dynComponent.dynForm.formValueChanges.subscribe(valuesChangesArgs => {
      if (this._activeWizardStep !== PpWizardStates.Details || this.dynComponent.dynForm.form.pristine ||
        (!this.dynComponent.dynForm.form.dirty && !this.dynComponent.dynForm.form.touched)) {
        return;
      }
      this.wizardDetailsProvider.onFormValueChanges(valuesChangesArgs);
    }));
    this.initSelection(PpWizardStates.Initial);
    this._subscriptions.push(
      this._statisticUpdateRequested.pipe(debounceTime(100)).subscribe(_ => {
        if (this.mapViewStore.getMapViewCurrentMenu() !== MapViewCurrentMenu.STATS ||
          this.mapViewStore.getMapViewMode() === MapViewMode.HIDE) {
          return;
        }
        this._activeProvider().onGenerateStatistic();
      })
    );
    this._subscriptions.push(this.items$.subscribe(_ => this._statisticUpdateRequested.emit()));
    this._subscriptions.push(
      combineLatest([
        this.mapViewStore.Listen(s => s.mode),
        this.mapViewStore.Listen(s => s.currentMenu)
      ]).subscribe(([mapMode, mapMenu]) => {
          if (mapMode !== MapViewMode.HIDE && mapMenu === MapViewCurrentMenu.STATS) {
            this._statisticUpdateRequested.emit();
          }
        }
      )
    );
    // Handle subscriptions in one place and forward them to the corresponding
    // wizard provider which is currently in charge of the action
    this._subscriptions.push(this.dynComponent.SelectedItems$.subscribe(items => {
      this._activeProvider()?.onGridSelectionChanged(items);
      this._statisticUpdateRequested.emit();
    }));
  }

  public ngOnDestroy(): void {
    window.removeEventListener('popstate', this.preventGoBack);
    this.statisticStore.removeStatisticData();
    this._subscriptions.forEach(s => s.unsubscribe());
    this.wizardSelectionProvider.onDestroy();
    this.wizardDetailsProvider.onDestroy();
    this.wizardOverviewProvider.onDestroy();
  }

  /**
   * Handles the state transitions between the wizard steps
   * and performs the corresponding actions defined in the wizard stateMachine/Model
   * @param newState the new wizard step. The user might have navigated back or forward
   * @private
   */
  private handleStateChange(newState: PpWizardStates): void {
    switch (this._activeWizardStep) {
      case PpWizardStates.Selection:
        this.selectionData = this.wizardSelectionProvider.onLeave();
        break;
      case PpWizardStates.Details:
        this.detailsData = this.wizardDetailsProvider.onLeave();
        break;
      case PpWizardStates.Overview:
        this.overviewData = this.wizardOverviewProvider.onLeave();
        break;
    }
    const oldState = this._activeWizardStep;
    this._activeWizardStep = newState;
    this.dynComponent.saveGridDescriptors(oldState);
    if (this._stateModel[oldState][newState]) {
      this._stateModel[oldState][newState](oldState);
    }
    // in order to restore sort/filter after user navigated through the wizard
    // we need to save and restore the grid descriptor for each wizard step
    this.dynComponent.loadGridDescriptors(newState);
  }

  /**
   * Handles whatever needs to happen when wizard switches to Selection
   * @param oldState indicates what the previous wizard step was (where the user comes from)
   * @private
   */
  private initSelection(oldState: PpWizardStates): void {
    this.items$ = this.wizardSelectionProvider.buildDataItems();
    this.buildUi(this.wizardSelectionProvider);
    // preselect currently selected fields (set by calling component)
    // or use previously selected field id's in case user came back from details step
    const selectedIds = oldState === PpWizardStates.Initial ?
      this.fieldStore.getSelectedFieldIds() :
      this.selectionData?.map(s => s.Id?.toString());
    if (selectedIds?.length > 0) {
      setTimeout(() => this.dynComponent.dynGrid?.gridPaging?.setSelectedKeys(selectedIds), 0);
    }
  }

  /**
   * Handles whatever needs to happen when wizard switches to Details
   * @param _oldState indicates what the previous wizard step was (where the user comes from)
   * @private
   */
  private initDetails(_oldState: PpWizardStates): void {
    this.productListColumns = this.wizardDetailsProvider.getProductListColumns();
    this.detailsData = this.wizardDetailsProvider.buildItem(this.selectionData);
    setTimeout(() => this.dynComponent.dynForm.form.patchValue(this.detailsData.FormData), 0);
    this.buildUi(this.wizardDetailsProvider);
    // trigger initial validations. Do not trigger validations if user
    setTimeout(() => this.dynComponent.dynForm.markAsTouched(), 0);
  }

  /**
   * Handles whatever needs to happen when wizard switches to Overview
   * @param _oldState indicates what the previous wizard step was (where the user comes from)
   * @private
   */
  private initOverview(_oldState: PpWizardStates): void {
    this.items$ = this.wizardOverviewProvider.buildDataItems(this.selectionData, this.detailsData, this.productListItems$?.value);
    this.buildUi(this.wizardOverviewProvider);
    if (this.detailsData.FormData.OperationModeKey === OperationModesEnum.PpTargetMapPerField
      || this.detailsData.FormData.OperationModeKey === OperationModesEnum.PPTargetMapPerPlan) {
      this._itemSubscription?.unsubscribe();
      this._itemSubscription = this.items$.pipe(
        debounceTime(10)
      ).subscribe((items: IPpPlanningWizardOverviewGridData[]) => {
        this._isContinueActive.next((items).every((i) => !i.ColumnLoading && !i.ErrorMessage));
        this._statisticUpdateRequested.emit();
      });
    } else {
      this._isContinueActive.next(true);
    }
    this._statisticUpdateRequested.emit();
  }

  /**
   * Handles whatever needs to happen when save is clicked
   * @private
   */
  private initSave(): void {
    const planBooks = this.overviewData.map(x => x.PlanBook);
    this.plantProtectionPlanBookStore.savePlanBooks(planBooks);
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  /**
   * Handles whatever needs to happen when leaving the wizard
   * @private
   */
  private initLeave(): void {
    setTimeout(() => this.formStore.closeForm(), 1);
  }

  /**
   * Generic function to generate/build UI depending on current wizard state.
   * @param wizardProvider The provider for the current wizard step
   * @private
   */
  private buildUi(wizardProvider: IPpWizardProvider<IPpPlanningWizardSelectionGridData | IPpPlanningWizardOverviewGridData, any>): void {
    if (!wizardProvider) {
      return;
    }
    wizardProvider.updateCaption();
    wizardProvider.updateFieldSets();
    this.columns$ = wizardProvider.getGridColumnConfig(this.detailsData);
    this.pager = wizardProvider.getPagerConfig(this.breadCrumbsColumns);
  }

  /**
   * Determines the active wizard provider based on current wizard step
   * @returns The active wizard provider for the current step
   * @private
   */
  private _activeProvider(): IPpWizardProvider<IPpPlanningWizardSelectionGridData | IPpPlanningWizardOverviewGridData, any> {
    switch (this._activeWizardStep) {
      case PpWizardStates.Selection:
        return this.wizardSelectionProvider;
      case PpWizardStates.Details:
        return this.wizardDetailsProvider;
      case PpWizardStates.Overview:
        return this.wizardOverviewProvider;
      default:
        return undefined;
    }
  }

  private preventGoBack(event): void {
    history.go(1);
  }
}
